// A person's profile as a PDF, saved from their page — T151 (28 Sep 2026).
//
// The brain API makes the PDF (services/bff/src/profile-pdf.ts, the same pages WhatsApp is sent)
// and answers it behind the login, so the page fetches the bytes with its bearer token and saves
// them itself: a plain link could not carry the token, and the response's own file name is not
// readable across origins without widening CORS for it. So the name is made here, the way the
// service makes it, and `profilePdf.test.ts` pins the two together.

/**
 * The service's `profileFileName`, restated: this app imports types from `@vera/bff` and never
 * code (api/core.ts). Plain letters, so every system the file lands on keeps the name.
 */
export function profileFileName(name: string): string {
  const plain = name
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^A-Za-z0-9 ]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
    .slice(0, 60);
  return `${plain || 'kadmon'} profile.pdf`;
}

/** Hands bytes to the browser as a file to keep, under a name. */
export type SaveFile = (bytes: ArrayBuffer, name: string, type: string) => void;

/**
 * Fetches the person's PDF and saves it. Throws what the fetch threw, an `ApiError`, for the
 * button to say; saves nothing then.
 */
export async function saveProfilePdf(
  fetchPdf: (
    candidateId: string,
    options?: { searchId?: string | null; roleId?: string },
  ) => Promise<ArrayBuffer>,
  person: { id: string; fullName: string | null },
  save: SaveFile,
  /**
   * T277: the Search to read them against; null for none, absent for the service's choice. T429h:
   * `{ roleId }` for a Proactive role.
   */
  against?: string | null | { roleId: string },
): Promise<void> {
  const bytes = await fetchPdf(
    person.id,
    against === undefined
      ? {}
      : typeof against === 'object' && against !== null
        ? { roleId: against.roleId }
        : { searchId: against },
  );
  save(bytes, profileFileName(person.fullName ?? ''), 'application/pdf');
}

/** The browser's way: a link to the bytes, clicked once and let go. */
export const saveInBrowser: SaveFile = (bytes, name, type) => {
  const url = URL.createObjectURL(new Blob([bytes], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  // A tick later, so the click has taken the URL before it is let go.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** One thing a person's profile can be read against, as the button's choice names it. */
export interface ProfileChoice {
  key: string;
  label: string;
  against: string | { roleId: string };
}

/** What pressing the button does: open the choice (several), or make the PDF against one or none. */
export type Press = { ask: true } | { ask: false; against: ProfileChoice['against'] | null };

export function pressOf(choices: readonly ProfileChoice[]): Press {
  if (choices.length > 1) return { ask: true };
  return { ask: false, against: choices[0]?.against ?? null };
}

/**
 * T429h: the Searches a person is on, then the Proactive roles they have been offered, as the
 * button lists them. A Search offered straight reads as a Search; a careers-page role as its id.
 */
export function profileChoices(candidate: {
  pipeline: readonly { searchId: string; searchTitle: string; clientName: string | null }[];
  proactiveRoles?: readonly {
    roleId: string | null;
    searchId: string | null;
    title: string;
    clientName: string;
  }[];
}): ProfileChoice[] {
  const name = (title: string, client: string | null) => [title, client].filter(Boolean).join(', ');
  return [
    ...candidate.pipeline.map((row) => ({
      key: `s:${row.searchId}`,
      label: name(row.searchTitle, row.clientName),
      against: row.searchId,
    })),
    ...(candidate.proactiveRoles ?? []).flatMap((r): ProfileChoice[] =>
      r.roleId !== null
        ? [
            {
              key: `r:${r.roleId}`,
              label: name(r.title, r.clientName),
              against: { roleId: r.roleId },
            },
          ]
        : r.searchId !== null && !candidate.pipeline.some((row) => row.searchId === r.searchId)
          ? [
              {
                key: `s:${r.searchId}`,
                label: `${name(r.title, r.clientName)} (Search)`,
                against: r.searchId,
              },
            ]
          : [],
    ),
  ];
}
